import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable, OnDestroy, inject } from "@angular/core";
import { fromEvent, Observable, Subscription, timer } from "rxjs";
import {
  debounceTime,
  delay,
  retryWhen,
  startWith,
  switchMap,
  tap,
} from "rxjs/operators";
import { UserService } from "./user.service";

export interface ConnectionState {
  /**
   * "True" if browser has network connection. Determined by Window objects "online" / "offline" events.
   */
  hasNetworkConnection: boolean;
  /**
   * "True" if browser has Internet access. Determined by heartbeat system which periodically makes request to heartbeat Url.
   */
  hasInternetAccess: boolean;
}

@Injectable({
  providedIn: "root",
})
export class CstConnectionService implements OnDestroy {
  private http = inject(HttpClient);
  private _userService = inject(UserService);

  private stateChangeEventEmitter = new EventEmitter<ConnectionState>();
  private currentState: ConnectionState = {
    hasInternetAccess: false,
    hasNetworkConnection: window.navigator.onLine,
  };
  private offlineSubscription: Subscription = null;
  private onlineSubscription: Subscription = null;
  private httpSubscription: Subscription = null;

  constructor() {
    this.checkNetworkState();
    this.checkInternetState();
  }

  private checkInternetState() {
    if (this.httpSubscription) {
      this.httpSubscription.unsubscribe();
    }

    this.httpSubscription = timer(0, 30000)
      .pipe(
        switchMap(() =>
          this.http.head("/", { responseType: "text" })
        ),
        retryWhen((errors) =>
          errors.pipe(
            tap((val) => {
              console.error("Http error:", val);
              this.currentState.hasInternetAccess = false;
              this.emitEvent();
            }),
            delay(1000)
          )
        )
      )
      .subscribe(() => {
        this.currentState.hasInternetAccess = true;
        this.emitEvent();
      });
  }

  private checkNetworkState() {
    this.onlineSubscription = fromEvent(window, "online").subscribe(() => {
      this.currentState.hasNetworkConnection = true;
      this.checkInternetState();
      this.emitEvent();
    });

    this.offlineSubscription = fromEvent(window, "offline").subscribe(() => {
      this.currentState.hasNetworkConnection = false;
      this.checkInternetState();
      this.emitEvent();
    });
  }
  private emitEvent() {
    this.stateChangeEventEmitter.emit(this.currentState);
  }

  ngOnDestroy(): void {
    try {
      this.offlineSubscription.unsubscribe();
      this.onlineSubscription.unsubscribe();
      this.httpSubscription.unsubscribe();
    } 
    catch (e) { console.error(e); }
  }

  monitor(reportCurrentState = true): Observable<ConnectionState> {
    return reportCurrentState
      ? this.stateChangeEventEmitter.pipe(
        debounceTime(300),
        startWith(this.currentState)
      )
      : this.stateChangeEventEmitter.pipe(debounceTime(300));
  }
}
